import { type AnyExtension, Editor, type EditorOptions, type JSONContent, type TextSerializer } from '@tiptap/core'
import type { Node } from '@tiptap/pm/model'
import { isString } from 'lodash-es'
import type { RequiredBy } from '@/types'
import type TmTiptapEditorExtensionsSet from '@/services/forms/tmTiptap/tmTiptapEditorExtensionsSet'
import type { TmEditorOutputPlugin } from '@/services/forms/tmTiptap/outputPlugins/types'
import type { TmEditorOptions } from '@/services/forms/tmTiptap/types'
import type TmTiptapEditorContentService from '@/services/forms/tmTiptap/tmTiptapEditorContentService'
import { isJsonContentEmptyParagraphOnly } from '@/services/forms/tmTiptap/utils/content'

export type TmTiptapEditorParams = Omit<EditorOptions, 'extensions'> & {
  id: string
  extensionsSet: TmTiptapEditorExtensionsSet
  options: TmEditorOptions
  tabindex?: number
}

export type TmTiptapEditorConstructorParams = RequiredBy<
  TmTiptapEditorParams,
  'id' | 'extensionsSet' | 'element' | 'options'
>

export class TmTiptapEditor extends Editor {
  protected readonly editorStorage = new Map<string, unknown>()

  protected readonly extensionsNames: string[] = []

  protected readonly outputPlugins: TmEditorOutputPlugin[]

  constructor(
    protected readonly tmTiptapParams: TmTiptapEditorConstructorParams,
    protected readonly contentService: TmTiptapEditorContentService,
  ) {
    const { id, extensionsSet, ...restOptions } = tmTiptapParams
    const { extensions, outputPlugins } = contentService.getExtensionsAndOutputPlugins(extensionsSet)

    super({
      ...restOptions,
      extensions,
    })

    this.outputPlugins = outputPlugins
    this.extensionsNames = Array.from(extensionsSet.getSet().values()).map((extension) => extension.name)
  }

  public getInstanceExtension<T extends AnyExtension>(extOrName: string | T): T | null {
    const extName = isString(extOrName) ? extOrName : extOrName.name
    return (this.extensionManager.extensions.find((ext) => ext.name === extName) as T) ?? null
  }

  public getJSON(): JSONContent {
    return this.contentService.processJsonWithPlugins(super.getJSON(), this.outputPlugins)
  }

  public getHTML(): string {
    return this.contentService.processHtmlWithPlugins(super.getHTML(), this.outputPlugins)
  }

  public getText(options?: { blockSeparator?: string; textSerializers?: Record<string, TextSerializer> }): string {
    return this.contentService.processPlainTextWithPlugins(super.getText(options), this.outputPlugins)
  }

  public getEditorId() {
    return this.getTmParams().id
  }

  public getTmParams() {
    return this.tmTiptapParams
  }

  public getEditorFieldOptions() {
    return this.getTmParams().options
  }

  public getActiveNodesAndMarks(): Set<string> {
    return new Set(this.extensionsNames.filter((extensionName) => this.isActive(extensionName)))
  }

  public getAllAttributesInCurrentSelection() {
    const { from, to } = this.state.selection
    const attributes: Record<string, any> = {}

    this.state.doc.nodesBetween(from, to, (node) => {
      if (node.attrs) {
        Object.assign(attributes, node.attrs)
      }
      for (const mark of node.marks) {
        Object.assign(attributes, mark.attrs ?? {})
      }
    })

    return attributes
  }

  public getSelectedText() {
    const { from, to } = this.state.selection
    return this.state.doc.textBetween(from, to, '')
  }

  public getNodeUnderCaret(): Node | null {
    const resolvedPos = this.state.selection.$from
    return this.state.doc.nodeAt(resolvedPos.pos) ?? null
  }

  public getExtensionsSet() {
    return this.getTmParams().extensionsSet.clone()
  }

  public isEmptyParagraphOnly() {
    const json = this.getJSON()
    return isJsonContentEmptyParagraphOnly(json)
  }

  public addToEditorStorage(key: string, item: unknown) {
    this.editorStorage.set(key, item)
  }

  public hasInEditorStorage(key: string) {
    return this.editorStorage.has(key)
  }

  public getFromEditorStorage<T>(key: string): T | null {
    return (this.editorStorage.get(key) as T) ?? null
  }
}
